// A conceptual folded ribbon, not the structure of a candidate or known protein.
const TAU = Math.PI * 2;

function center(t) {
  const radius = 1.14 + 0.38 * Math.cos(3 * t);
  return [radius * Math.cos(2 * t), radius * Math.sin(2 * t), 0.63 * Math.sin(3 * t)];
}

function normalize(vector) {
  const length = Math.hypot(...vector) || 1;
  return vector.map((value) => value / length);
}

function cross(a, b) {
  return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
}

function rotate([x, y, z]) {
  const ax = 0.84;
  const ay = -0.28;
  const az = -0.48;
  const y1 = y * Math.cos(ax) - z * Math.sin(ax);
  const z1 = y * Math.sin(ax) + z * Math.cos(ax);
  const x2 = x * Math.cos(ay) + z1 * Math.sin(ay);
  const z2 = -x * Math.sin(ay) + z1 * Math.cos(ay);
  return [x2 * Math.cos(az) - y1 * Math.sin(az), x2 * Math.sin(az) + y1 * Math.cos(az), z2];
}

function ribbonPoint(t, across, shell = 0) {
  const origin = center(t);
  const next = center(t + 0.0001);
  const tangent = normalize(next.map((value, index) => value - origin[index]));
  const normal = normalize(cross(tangent, [0, 0, 1]));
  const binormal = cross(tangent, normal);
  const twist = t * 1.5 + 0.3;
  const width = across * (0.19 + 0.045 * Math.sin(t * 3 + 0.6));
  return rotate(origin.map((value, index) => value
    + width * (normal[index] * Math.cos(twist) + binormal[index] * Math.sin(twist))
    + shell * (-normal[index] * Math.sin(twist) + binormal[index] * Math.cos(twist))));
}

export const molecularPoints = (() => {
  const points = [];
  for (let step = 0; step < 400; step += 1) {
    const t = step / 400 * TAU;
    for (let across = -8; across <= 8; across += 1) {
      for (let layer = -1; layer <= 1; layer += 2) {
        const position = ribbonPoint(t, across / 8, layer * 0.022);
        points.push({ position, glint: (Math.sin(step * 1.71 + across * 3.1) + 1) / 2 });
      }
    }
  }
  return points;
})();

// Compact, resolution-independent fallback while the canvas is prepared.
export const molecularContours = Array.from({ length: 88 }, (_, index) => {
  const t = index / 88 * TAU;
  const points = Array.from({ length: 17 }, (_, across) => ribbonPoint(t, (across - 8) / 8));
  return {
    depth: points[8][2],
    path: points.map(([x, y], pointIndex) => `${pointIndex ? "L" : "M"}${(200 + x * 105).toFixed(2)} ${(200 - y * 105).toFixed(2)}`).join(" "),
  };
}).sort((a, b) => a.depth - b.depth);

function rgb(hex) {
  const value = hex.trim().replace("#", "");
  return [0, 2, 4].map((index) => parseInt(value.slice(index, index + 2), 16));
}

export function paintMolecularStill(canvas, element) {
  const bounds = element.getBoundingClientRect();
  if (!bounds.width || !bounds.height) return false;
  const context = canvas.getContext("2d");
  if (!context) return false;
  const ratio = Math.min(window.devicePixelRatio || 1, 1.75);
  canvas.width = Math.round(bounds.width * ratio);
  canvas.height = Math.round(bounds.height * ratio);
  context.setTransform(ratio, 0, 0, ratio, 0, 0);
  context.clearRect(0, 0, bounds.width, bounds.height);
  const styles = getComputedStyle(element);
  const low = rgb(styles.getPropertyValue("--molecular-deep") || "#174783");
  const high = rgb(styles.getPropertyValue("--molecular-bright") || "#4fa5ec");
  const scale = Math.min(bounds.width, bounds.height) / 3.8;
  const particles = [...molecularPoints].sort((a, b) => a.position[2] - b.position[2]);
  for (const { position: [x, y, z], glint } of particles) {
    const light = Math.max(0, Math.min(1, (z + 1.45) / 3 + glint * 0.14));
    const color = low.map((value, index) => Math.round(value + (high[index] - value) * light));
    context.fillStyle = `rgba(${color.join(",")},${0.54 + light * 0.42})`;
    context.beginPath();
    context.arc(bounds.width / 2 + x * scale, bounds.height / 2 - y * scale, Math.max(0.68, scale * (0.0067 + glint * 0.0022)), 0, TAU);
    context.fill();
  }
  return true;
}
